import { z } from 'zod';
import { AgentBehaviorConfigSchema } from './agent-behavior-config.js';
import { ToolRefWarningSchema } from './tool-ref.js';
/**
 * Which surface an agent serves (DEV-16807 unified identity, reconciled
 * 2026-07-13 with the ORCHESTRATION-kind collapse). As of DEV-16943 workflow
 * `llm-agent` nodes are self-contained and reference no Agent row, so the old
 * STANDARD ("plain workflow persona") kind was retired (DEV-16945). The DB
 * keeps its historical rows under an internal LEGACY tombstone (soft-deleted),
 * but the API never emits or accepts it — hence it is absent from this schema.
 *   - ORCHESTRATION — backs a persistent-agent thread (see
 *     `OrchestrationAgentConfig`, its 1:1 extended config in
 *     `persistent-agent.ts`). Set once at create time — the generic Agent
 *     create endpoint never accepts ORCHESTRATION as input; only the
 *     persistent-agent creation flow produces one, so every ORCHESTRATION
 *     Agent is guaranteed to have a config row.
 *   - REFINEMENT — the seeded refinement agent (code-managed).
 *   - CHAT — the seeded chat agents (code-managed; conceptually an
 *     orchestrator with broad talk permissions per the 2026-07-13 decision).
 * Deliberately NO `PERSISTENT` value — "persistent" was judged a misnomer;
 * ORCHESTRATION is that concept's name.
 */
export const AgentKindSchema = z.enum(['ORCHESTRATION', 'REFINEMENT', 'CHAT']);
/**
 * Whether a `request_human_input` ask pauses its thread (DEV-16877
 * follow-up). BLOCKING (default) — existing behavior: thread goes
 * WAITING_FOR_INPUT until answered/expired/cancelled. NON_BLOCKING — the
 * agent keeps working; the answer arrives later as an ordinary wake event.
 * Only meaningful for ORCHESTRATION-kind agents with `hitlAllowed`.
 */
export const PersistentAgentElicitationModeSchema = z.enum(['BLOCKING', 'NON_BLOCKING']);
/**
 * Agent — an LLM agent definition, stored as a DB row, backing a
 * persistent-agent surface (ORCHESTRATION/REFINEMENT/CHAT). As of DEV-16943
 * workflow `llm-agent` nodes are self-contained and no longer reference an
 * Agent row, so agents are minted only by the persistent-agent creation flow
 * and the seed catalog (the generic CRUD create path is deprecated,
 * DEV-16945).
 *
 * Two flavors share this contract (DEV-16807): per-org agents (`orgId` set,
 * `managed: false`) and global managed system agents (`orgId: null`,
 * `managed: true` — well-known ids like `agt_theopolis`, read-only through
 * the CRUD API; update/delete answer 403).
 */
export const AgentSchema = z.object({
    id: z.string(),
    /** Null only for global managed system agents; set for user agents. */
    orgId: z.string().nullable(),
    name: z.string(),
    /** Surface this agent serves (see AgentKindSchema). */
    kind: AgentKindSchema,
    /**
     * True for code-managed system agents (seeded, PR-reviewed config).
     * Clients should render these read-only — mutations 403.
     */
    managed: z.boolean(),
    description: z.string().nullable(),
    modelConfigId: z.string().nullable(),
    instructions: z.string(),
    toolRefs: z.array(z.string()),
    /**
     * Optional per-MCP-connection tool allowlist. Keyed by an MCPConnection name
     * (as it appears in `toolRefs`); the value is the set of tool names from that
     * server the agent is allowed to use.
     *
     * Semantics: when a connection has NO entry here, ALL of its advertised tools
     * are exposed (backwards-compatible default). When an entry IS present, only
     * the listed tool names pass through at resolution time — e.g. to give an
     * agent only the read-only subset of a server's tools. Entries that name a
     * platform (non-MCP) toolRef, or a connection not referenced by `toolRefs`,
     * are simply inert.
     */
    toolAllowlists: z.record(z.string(), z.array(z.string())),
    outputSchema: z.record(z.string(), z.unknown()).nullable(),
    config: z.record(z.string(), z.unknown()),
    /**
     * Whether this agent is permitted to pause for HITL when its instructions
     * (or, for deterministic agents, its code) emit a pause request.
     * Default false — opt in per agent. (DEV-15886 / EU-20)
     */
    hitlAllowed: z.boolean(),
    /**
     * Whether `request_human_input` asks from this agent pause the thread
     * (BLOCKING, default) or let it keep working (NON_BLOCKING). Only read
     * when `hitlAllowed` is true. (DEV-16877 follow-up)
     */
    elicitationMode: PersistentAgentElicitationModeSchema,
    /**
     * Whether this agent may use a model-bound `describe_image` tool to "see"
     * an image URL it's handed. Models can't accept an image inline through the
     * text prompt, so without this an agent given an image URL just says "I
     * can't view images". When true, a `describe_image(imageUrl, prompt?)` tool
     * — bound to the agent's own model — is exposed so it can run a multimodal
     * completion and feed the description back into the loop. Default false.
     */
    imageDescriptionAllowed: z.boolean(),
    /**
     * Whether this agent may use a model-bound `describe_video` tool to "see" a
     * video URL it's handed. The video sibling of `imageDescriptionAllowed`:
     * when true, a `describe_video(videoUrl, prompt?)` tool is exposed that
     * extracts the video's first frame and runs a multimodal completion on the
     * agent's own model, feeding the description back into the loop. A still
     * frame carries less than the clip, but needs no video-native provider.
     * Default false.
     */
    videoDescriptionAllowed: z.boolean(),
    /** Collection membership for ORCHESTRATION agents; null for every other kind. */
    orchestrationGroupId: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    /**
     * Save-time tool-ref validation warnings (DEV-16944). Present (non-empty)
     * only on update responses whose `toolRefs` contain entries that resolve
     * to neither a platform tool nor an MCP connection. (Generic agent create
     * is retired per DEV-16945 — POST /v1/agents 400s — so update is the only
     * write that can carry these.) Advisory only — the save always succeeds.
     * Additive field; absent on reads.
     */
    warnings: z.array(ToolRefWarningSchema).optional(),
});
export const CreateAgentInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    modelConfigId: z.string().optional(),
    instructions: z.string().min(1),
    toolRefs: z.array(z.string()).default([]),
    /**
     * Per-MCP-connection tool allowlist (see `AgentSchema.toolAllowlists`).
     * Omit (or pass `{}`) to expose every tool of every referenced server.
     */
    toolAllowlists: z.record(z.string(), z.array(z.string().min(1))).default({}),
    outputSchema: z.record(z.string(), z.unknown()).optional(),
    config: z.record(z.string(), z.unknown()).default({}),
    /**
     * Typed agent BEHAVIOR config (WS-D) — memory, reflection, goal, continuity,
     * code-mode. Validated + merged into `config` by the service on write (its
     * sub-object keys match the host's `config.*` keys 1:1), so this is the
     * discoverable, Swagger-documented door onto knobs that previously required
     * hand-writing raw `config` JSON. Omit to leave `config` untouched; keys set
     * here overwrite the same keys in `config`.
     */
    behaviorConfig: AgentBehaviorConfigSchema.optional(),
    /** Opt the agent into HITL pause; defaults to false. */
    hitlAllowed: z.boolean().optional(),
    /**
     * Blocking vs non-blocking `request_human_input` asks; defaults to
     * BLOCKING. Only meaningful when `hitlAllowed` is true. (DEV-16877 follow-up)
     */
    elicitationMode: PersistentAgentElicitationModeSchema.optional(),
    /** Expose the model-bound `describe_image` tool; defaults to false. */
    imageDescriptionAllowed: z.boolean().optional(),
    /** Expose the model-bound `describe_video` tool; defaults to false. */
    videoDescriptionAllowed: z.boolean().optional(),
});
// `outputSchema` needs explicit nullability on update (the Create shape
// only accepts `undefined` via `.optional()`). Sending `null` is how the
// console clears a previously-set structured-output contract — without
// this override, Zod 400s before the service can run. Other fields keep
// `.partial()`'s "omit to leave unchanged" semantics.
export const UpdateAgentInputSchema = CreateAgentInputSchema.partial().extend({
    outputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
});
/**
 * Ad-hoc test invocation of an agent — the payload for `POST /v1/agents/:id/test`.
 *
 * A lightweight sandbox over the same machinery the `llm-agent` node uses
 * (resolveInstructions → resolveTools → buildAgent → stream), minus the
 * run/node-execution persistence and input-schema validation. Nothing is
 * recorded (no Run, NodeExecution or CostRecord) — it's purely for trying
 * an agent out from the console.
 *
 *  - `variables` fills the Handlebars `{{tokens}}` in the agent's
 *    instructions (the system prompt is rendered with `interpolate: true`).
 *  - `userMessage` is the first user turn sent to the agent.
 *  - `maxSteps` caps the tool-call loop (defaults to the runner's default).
 *  - `structured`, when omitted, defaults to "structured if the agent has an
 *    `outputSchema`"; set explicitly to force/skip the structuring pass.
 */
export const TestAgentInputSchema = z.object({
    variables: z.record(z.string(), z.string()).default({}),
    userMessage: z.string().min(1),
    maxSteps: z.number().int().positive().max(50).optional(),
    structured: z.boolean().optional(),
});
/** `GET /v1/agents` row shape — same as {@link AgentSchema}. */
export const AgentListItemSchema = AgentSchema;
//# sourceMappingURL=agent.js.map